import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { APP_VERSION } from './version';

// THE one place that talks to Google's In-App Updates API. ForceUpdateGuard
// (the hard block), the version card on My Profile and the top bar all read
// this, so the Play Store is asked once per launch and all three agree.
export const PACKAGE_NAME = 'com.smartify.mehfil';

export const isAndroidApp = () => Capacitor.getPlatform() === 'android';

// What we can SAY about the installed build:
//   'latest'  — the Play Store has nothing newer
//   'update'  — a newer version is live (the guard is blocking, or will)
//   'unknown' — we could not ask: a browser, a sideloaded APK, offline, or any
//               Play API error. NEVER shown as "old" — we do not know.
export const UPDATE_STATE = { LATEST: 'latest', UPDATE: 'update', UNKNOWN: 'unknown' };

// `3.5.1 (28)` as written into REACT_APP_VERSION by scripts/write-version.js.
// On an APK carrying an OTA bundle this is the BUNDLE's version, which is the
// honest answer to "what am I running".
export const installedVersion = () => APP_VERSION;

// Asked once per launch and remembered — the Play check is a network round trip
// and three screens want the answer. `refresh()` forces it again.
let pending = null;
let cached = null;

const ask = async () => {
  if (!isAndroidApp()) {
    return { state: UPDATE_STATE.UNKNOWN, web: true, installed: installedVersion() };
  }
  try {
    const { AppUpdate, AppUpdateAvailability } = await import('@capawesome/capacitor-app-update');
    const info = await AppUpdate.getAppUpdateInfo();
    const available = info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE;
    return {
      state: available ? UPDATE_STATE.UPDATE : UPDATE_STATE.LATEST,
      installed: installedVersion(),
      // On ANDROID the Play Store only tells us the versionCode of the new
      // build, never its versionName (that field is iOS-only) — so the card
      // prints the code and never invents a "3.6.0".
      installedCode: info.currentVersionCode || '',
      availableCode: info.availableVersionCode || '',
      immediateUpdateAllowed: !!info.immediateUpdateAllowed,
    };
  } catch (err) {
    // Fail-open, exactly as the guard does: a sideloaded APK (in-app updates
    // only answer for a build installed FROM Play), offline, or a Play error.
    return {
      state: UPDATE_STATE.UNKNOWN,
      installed: installedVersion(),
      error: (err && err.message) || String(err),
    };
  }
};

export function playUpdateInfo({ refresh = false } = {}) {
  if (!refresh && cached) return Promise.resolve(cached);
  if (!refresh && pending) return pending;
  pending = ask().then((res) => { cached = res; pending = null; return res; });
  return pending;
}

export const openPlayStore = async () => {
  try {
    const { AppUpdate } = await import('@capawesome/capacitor-app-update');
    await AppUpdate.openAppStore({ androidPackageName: PACKAGE_NAME });
  } catch { /* nothing else we can do from here */ }
};

/**
 * useAppUpdate — the version line for any screen. Returns the cached answer
 * (so mounting it in three places costs one Play call) plus `checking` and a
 * `check()` that asks again.
 */
export function useAppUpdate() {
  const [info, setInfo] = useState(cached);
  const [checking, setChecking] = useState(!cached);

  useEffect(() => {
    let dead = false;
    playUpdateInfo().then((res) => { if (!dead) { setInfo(res); setChecking(false); } });
    return () => { dead = true; };
  }, []);

  const check = async () => {
    setChecking(true);
    const res = await playUpdateInfo({ refresh: true });
    setInfo(res);
    setChecking(false);
    return res;
  };

  return {
    info,
    checking,
    check,
    installed: (info && info.installed) || installedVersion(),
    state: (info && info.state) || UPDATE_STATE.UNKNOWN,
    availableCode: (info && info.availableCode) || '',
    installedCode: (info && info.installedCode) || '',
    isWeb: !!(info && info.web),
    updateAvailable: !!(info && info.state === UPDATE_STATE.UPDATE),
    openPlayStore,
  };
}
